<template>
    <div v-if="showTip" class="mobile-download-tip">
        <div class="content">
            <img class="content-icon" src="@/public/img/tip-info.svg" alt="" />
            <div class="content-title">
                {{ $t("title") }}
            </div>
            <div class="content-text">
                {{ $t("tip-content") }}
            </div>
            <a class="content-submit" :href="jumpLink" rel="nofollow" target="_blank">
                {{ $t("btn-submit") }}
            </a>
            <div class="content-cancel" @click="showTip = false">
                {{ $t("btn-cancel") }}
            </div>
        </div>
    </div>
</template>

<script>
    import { Component, Vue } from "vue-property-decorator";
    import dayjs from "dayjs";

    @Component({})
    export default class MobileDownloadTip extends Vue {
        showTip = false;
        jumpLink = "";

        isMobileDevice() {
            const ua = navigator.userAgent;
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
            const isTablet = /iPad|Tablet|PlayBook|Kindle/i.test(ua);
            return isMobile && !isTablet;
        }

        isIOS() {
            return /iPhone|iPad|iPod|iOS/i.test(navigator.userAgent);
        }

        mounted() {
            // https://app.adjust.com/1fvgaahj_1fw6mj2b
            this.jumpLink = this.isIOS()
                ? "https://apps.apple.com/us/app/edrawmax-ai-diagram-maker/id1663182670"
                : "https://play.google.com/store/apps/details?id=com.edrawsoft.edrawmax.oversea.mobile";

            // 每天只提示一次
            const dayStr = localStorage.getItem("mobileDownloadTip");
            if (dayStr !== dayjs().format("YYYY-MM-DD")) {
                localStorage.setItem("mobileDownloadTip", dayjs().format("YYYY-MM-DD"));
                this.showTip = !this.isPC && !window.EDCONFIG.IS_SDK && !this.internal && this.isMobileDevice();
            }
        }
    }
</script>

<style lang="less" scoped>
    .mobile-download-tip {
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.4);
        .content {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            width: 320px;
            padding: 0 20px;
            border-radius: 16px;
            background: var(--bg-color-tertiary);
            box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.08);
            .content-icon {
                width: 40px;
                height: 40px;
                margin: 40px 0 24px 0;
            }
            .content-title {
                margin-bottom: 12px;
                font-size: 16px;
                line-height: 20px;
                font-weight: 700;
                color: var(--color-text-primary, rgba(34, 34, 34, 1));
            }
            .content-text {
                margin-bottom: 24px;
                text-align: center;
                font-size: 16px;
                line-height: 24px;
                color: var(--color-text-secondary, rgba(0, 0, 0, 0.5));
            }
            .content-submit,
            .content-cancel {
                display: flex;
                align-items: center;
                justify-content: center;
                flex-direction: column;
                height: 48px;
                width: 100%;
                margin-bottom: 12px;
                border-radius: 6px;
                font-size: 16px;
            }
            .content-submit {
                color: var(--color-static-white, rgba(255, 255, 255, 1));
                background: var(--color-accent-primary-default, rgba(1, 127, 255, 1));
            }
            .content-cancel {
                color: var(--color-text-secondary, rgba(0, 0, 0, 0.5));
            }
        }
    }
</style>

<i18n>
{
    "zh": {
        "title": "免费下载亿图图示手机App",
        "tip-content": "亿图图示网页端未对移动设备进行适配，推荐下载手机App获得更优体验",
        "btn-submit": "下载App",
        "btn-cancel": "忽略"
    },
    "en": {
        "title": "Download EdrawMax App for Free",
        "tip-content": "EdrawMax Online is not optimized for mobile devices. For a better experience, we recommend downloading the EdrawMax app.",
        "btn-submit": "Download App",
        "btn-cancel": "Ignore"
    },
    "fr": {
        "title": "Téléchargez gratuitement l'application EdrawMax",
        "tip-content": "EdrawMax Online n'est pas optimisé pour les appareils mobiles. Pour une meilleure expérience, nous vous recommandons de télécharger l'application EdrawMax.",
        "btn-submit": "Télécharger l'application",
        "btn-cancel": "Ignorer"
    },
    "de": {
        "title": "EdrawMax-App kostenlos herunterladen",
        "tip-content": "EdrawMax Online ist nicht für mobile Geräte optimiert. Für ein besseres Erlebnis empfehlen wir, die EdrawMax-App herunterzuladen.",
        "btn-submit": "App herunterladen",
        "btn-cancel": "Ignorieren"
    },
    "jp": {
        "title": "EdrawMaxアプリを無料でダウンロード",
        "tip-content": "EdrawMaxオンライン版はモバイルデバイス向けに最適化されていません。快適にご利用いただくには、EdrawMaxアプリのダウンロードをお勧めします。",
        "btn-submit": "アプリをダウンロード",
        "btn-cancel": "無視"
    },
    "es": {
        "title": "Descargar gratis la aplicación EdrawMax",
        "tip-content": "EdrawMax Online no está optimizada para dispositivos móviles. Para una mejor experiencia, recomendamos descargar la aplicación EdrawMax.",
        "btn-submit": "Descargar aplicación",
        "btn-cancel": "Ignorar"
    },
    "pt": {
        "title": "Baixe o app EdrawMax gratuitamente",
        "tip-content": "O EdrawMax Online não é otimizado para dispositivos móveis. Para uma melhor experiência, recomendamos baixar o app EdrawMax.",
        "btn-submit": "Baixar app",
        "btn-cancel": "Ignorar"
    },
    "it": {
        "title": "Scarica l'app EdrawMax gratuitamente",
        "tip-content": "EdrawMax Online non è ottimizzato per i dispositivi mobili. Per una migliore esperienza, si consiglia di scaricare l'app EdrawMax.",
        "btn-submit": "Scarica l'app",
        "btn-cancel": "Ignora"
    }
}
</i18n>
